import { SyntheticEvent, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';
import { get } from 'lodash';

import {
  StudentsDataListFilterProps,
  USStatesListResponse
} from 'entities/Dataset/sdk';

import Autocomplete from 'components/Autocomplete';
import Button from 'components/Button';
import FormControl from 'components/FormControl';
import FormLabel from 'components/FormLabel';
import MenuItem from 'components/MenuItem';
import Select from 'components/Select';
import MuiTextField from 'components/TextField';

interface StudentsListFiltersProps {
  usStates: Array<USStatesListResponse> | undefined;
  filters: StudentsDataListFilterProps;
  onFilter: (filters: StudentsDataListFilterProps) => void;
  handleExport: () => void;
}

const StudentsListFilters = ({
  usStates,
  onFilter,
  filters,
  handleExport
}: StudentsListFiltersProps) => {
  const [gender, setGender] = useState<string>(filters.gender);
  const [grade, setGrade] = useState<string>(filters.grade);
  const [ethnicity, setEthnicity] = useState<string>(filters.ethnicity);
  const [state, setState] = useState<string>(filters.state);
  const [isLowIncome, setIsLowIncome] = useState<string>(filters.is_low_income);
  const [isOnIEP, setIsOnIEP] = useState<string>(
    filters.is_on_individualized_education_plan
  );
  const [assignmentName, setAssignmentName] = useState<string>(
    filters.assignment_name
  );
  const [standardCode, setStandardCode] = useState<string>(
    filters.standard_code
  );
  const [englishLearnerStatus, setEnglishLearnerStatus] = useState<string>(
    filters.english_learner_status
  );

  const onGenderChange = (event: SelectChangeEvent) => {
    const newGender = event.target.value as string;
    setGender(newGender);
    onFilter({
      gender: newGender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onGradeChange = (event: SelectChangeEvent) => {
    const newGrade = event.target.value as string;
    setGrade(newGrade);
    onFilter({
      gender,
      grade: newGrade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onRaceChange = (event: SelectChangeEvent) => {
    const newRace = event.target.value as string;
    setEthnicity(newRace);
    onFilter({
      gender,
      grade,
      ethnicity: newRace,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onStateChange = (
    _event: SyntheticEvent,
    value: USStatesListResponse | null
  ) => {
    const newState = get(value, 'abbreviation', '');
    setState(newState);
    onFilter({
      gender,
      grade,
      ethnicity,
      state: newState,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onIsLowIncomeChange = (event: SelectChangeEvent) => {
    const newIsLowIncome = event.target.value as string;
    setIsLowIncome(newIsLowIncome);
    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: newIsLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onIsOnIEPChange = (event: SelectChangeEvent) => {
    const newIsOnIEP = event.target.value as string;
    setIsOnIEP(newIsOnIEP);
    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: newIsOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onAssignmentNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newAssignmentName = event.target.value;
    setAssignmentName(newAssignmentName);

    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: newAssignmentName,
      standard_code: standardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onStandardCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStandardCode = event.target.value;
    setStandardCode(newStandardCode);

    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: newStandardCode,
      english_learner_status: englishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  const onEnglishLearnerStatusChange = (event: SelectChangeEvent) => {
    const newEnglishLearnerStatus = event.target.value as string;
    setEnglishLearnerStatus(newEnglishLearnerStatus);
    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      assignment_name: assignmentName,
      standard_code: standardCode,
      english_learner_status: newEnglishLearnerStatus,
      limit: filters.limit,
      offset: filters.offset
    });
  };

  return (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-5">
      <div className="flex w-full flex-wrap items-center gap-5">
        <FormControl className="w-64">
          <FormLabel id="state-label">State</FormLabel>

          <Autocomplete
            className="bg-white"
            disablePortal
            options={usStates ?? []}
            renderInput={(params) => <MuiTextField {...params} />}
            getOptionLabel={(state) => `${state.name} (${state.abbreviation})`}
            onChange={onStateChange}
          />
        </FormControl>
        <FormControl className="w-64">
          <FormLabel id="grade-label">Grade</FormLabel>
          <Select
            className="bg-white"
            labelId="grade-label"
            value={grade}
            onChange={onGradeChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">1</MenuItem>
            <MenuItem value="2">2</MenuItem>
            <MenuItem value="3">3</MenuItem>
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="5">5</MenuItem>
            <MenuItem value="6">6</MenuItem>
            <MenuItem value="7">7</MenuItem>
            <MenuItem value="8">8</MenuItem>
            <MenuItem value="9">9</MenuItem>
            <MenuItem value="10">10</MenuItem>
            <MenuItem value="11">11</MenuItem>
            <MenuItem value="12">12</MenuItem>
            <MenuItem value="995">Missing grade</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="gender-label">Gender</FormLabel>
          <Select
            className="bg-white"
            labelId="gender-label"
            value={gender}
            onChange={onGenderChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="M">Male</MenuItem>
            <MenuItem value="F">Female</MenuItem>
            <MenuItem value="U">Unknown</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="ethnicity-label">Ethnicity</FormLabel>
          <Select
            className="bg-white"
            labelId="ethnicity-label"
            value={ethnicity}
            onChange={onRaceChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="asian">Asian</MenuItem>
            <MenuItem value="white">White</MenuItem>
            <MenuItem value="hispanic">Hispanic/Latinx</MenuItem>
            <MenuItem value="black">Black</MenuItem>
            <MenuItem value="two_or_more">Two or more races</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="low-income-label">Is low income</FormLabel>
          <Select
            className="bg-white"
            labelId="low-income-label"
            value={isLowIncome}
            onChange={onIsLowIncomeChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
        <FormControl className="w-64">
          <FormLabel id="iep-label">IEP</FormLabel>
          <Select
            className="bg-white"
            labelId="iep-label"
            value={isOnIEP}
            onChange={onIsOnIEPChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
        <FormControl className="w-64">
          <FormLabel id="assignment-label">Assignment name</FormLabel>
          <MuiTextField
            className="bg-white"
            id="assignment-label"
            value={assignmentName}
            onChange={onAssignmentNameChange}
          />
        </FormControl>
        <FormControl className="w-64">
          <FormLabel id="standard-code-label">Standard</FormLabel>
          <MuiTextField
            className="bg-white"
            id="standard-code-label"
            value={standardCode}
            onChange={onStandardCodeChange}
          />
        </FormControl>
        <FormControl className="w-64">
          <FormLabel id="lep-label">LEP</FormLabel>
          <Select
            className="bg-white"
            labelId="lep-label"
            value={englishLearnerStatus}
            onChange={onEnglishLearnerStatusChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="partime bl, should be fulltime bl">
              partime bl, should be fulltime bl
            </MenuItem>
            <MenuItem value="special ed">special ed</MenuItem>
            <MenuItem value="waiver">waiver</MenuItem>
            <MenuItem value="part-time biling">part-time biling</MenuItem>
            <MenuItem value="dual lang">dual lang</MenuItem>
            <MenuItem value="tpi student">tpi student</MenuItem>
            <MenuItem value="tbe student">tbe student</MenuItem>
            <MenuItem value="lep">lep</MenuItem>
            <MenuItem value="not lep">not lep</MenuItem>
            <MenuItem value="exited program">exited program</MenuItem>
            <MenuItem value="no access, remained dl">
              no access, remained dl
            </MenuItem>
            <MenuItem value="not eligible">not eligible</MenuItem>
          </Select>
        </FormControl>
      </div>

      <Button className="w-32 leading-[0]" onClick={handleExport}>
        Export
      </Button>
    </div>
  );
};

export default StudentsListFilters;
